import { useEffect, useMemo, useState } from "react";
import { Box, Grid, Button, TextField, Divider, Drawer, Link, DialogActions, Alert, CircularProgress, InputAdornment } from "@mui/material";
import { styled } from '@mui/material/styles';
import { CentralDatePicker, CentralDropdown, CentralTextField, Ico_Close, Ico_NewWindow, useSnackbar } from '@sagecentral/core-ui';
import { ITaskOverview } from "models/tasks";
import { useTaskContext, useTaskFieldUpdater, useTaskPermissions, useTaskProblemDetails, useTaskProblemUpdater, useTaskStatusOptions } from "hooks/tasks";
import TaskProgressPill from "../TaskProgressPill";
import { ITaskTemplateResouce } from "models/tasks/ITaskTemplate";
import { TaskProblemLogUpdateData } from "hooks/tasks/useTaskProblemUpdater";
import { KeyGrid } from "components/shared";
import TaskProblemGrid from "../grid/TaskProblemGrid";
import moment from "moment";

const UrgentWrapper = styled(Box)(({ theme }) => ({
    color: theme.palette.error.main,
    fontWeight: 'bold',
    display: 'inline-block'
}));

const priorityOptions: any[] = [
    { id: -1, name: 'None', display: (<em>None</em>) },
    { id: 1, name: 'Urgent', display: <UrgentWrapper>Urgent</UrgentWrapper> },
    { id: 2, name: 'High', display: 'High' },
    { id: 3, name: 'Medium', display: 'Medium' },
    { id: 4, name: 'Low', display: 'Low' }
];

const TaskOverview = () => {

    const taskContext = useTaskContext();
    const permissions = useTaskPermissions();
    const { task, template, updateTaskOverview, refreshTemplate } = taskContext;
    const [addProblemModal, setAddProblemModal] = useState(false);
    const [problemToUpdate, setProblemToUpdate] = useState<TaskProblemLogUpdateData | undefined>(undefined);
    const [problemError, setProblemError] = useState<string>('');

    const { getOptionsForStatus } = useTaskStatusOptions();

    const { displaySnackbar } = useSnackbar();

    const handleFieldError = (error: string) => {
        displaySnackbar('warning', error);
    };
    const handleFieldSuccess = (data: ITaskOverview, message: string, shouldUpdateTemplate?: boolean) => {
        data.updateTemplate = shouldUpdateTemplate;
        if (data) updateTaskOverview(data);
        displaySnackbar('success', `Change has been successful. ${message}`);
    };
    const [saveStatus, statusWorking] = useTaskFieldUpdater('status', (data: ITaskOverview) => {
        handleFieldSuccess(data, "The status has been updated.", true);
        refreshTemplate();
    }, handleFieldError)
    const [saveDuration, durationWorking] = useTaskFieldUpdater('duration', (data: ITaskOverview) => { handleFieldSuccess(data, "The dates have been adjusted accordingly.") }, handleFieldError);
    const [saveStartDate, startDateWorking] = useTaskFieldUpdater('start-date', (data: ITaskOverview) => { handleFieldSuccess(data, "The dates have been adjusted accordingly.") }, handleFieldError);
    const [saveEndDate, endDateWorking] = useTaskFieldUpdater('end-date', (data: ITaskOverview) => { handleFieldSuccess(data, "Task duration has been adjusted accordingly. ") }, handleFieldError);
    const [saveResponsibility, responsibilityWorking] = useTaskFieldUpdater('responsibility', (data: ITaskOverview) => { handleFieldSuccess(data, "The responsibility has been updated.") }, handleFieldError);
    const [saveResource, resourceWorking] = useTaskFieldUpdater('resource', (data: ITaskOverview) => { handleFieldSuccess(data, "The resource has been updated.", true) }, handleFieldError);

    const [problemTextField, setProblemTextField] = useState<string | undefined>(undefined);

    const [result, saveProblemLoading, saveProblemError] = useTaskProblemUpdater(problemToUpdate);
    const [problemLog, total, loading, error, hasViewProblemLogPermission, hasAddProblemFlagPermission, hasRemoveProblemFlagPermission] =
        useTaskProblemDetails(task?.id, (!saveProblemLoading && problemToUpdate?.problemLogId === undefined));

    const resourceOptions = useMemo<ITaskTemplateResouce[]>(() => {
        if (!template) return [];
        if (!task) return [];

        const index = template.responsibilities.findIndex(r => r.id === task?.responsibilityId);
        if (index === -1) return [];
        return template.responsibilities[index].resources || [];
    }, [task, task?.responsibilityId, task?.resourceId, template]);

    useEffect(() => {
        // force refresh template
    }, [task?.statusId]);

    const statusOptions: any[] = useMemo<any[]>(() => {
        if (task === null) return [];
        return getOptionsForStatus(task.statusId, task.isMandatory, false);
    }, [task?.statusId, permissions]);

    const renderPriority: (value: any) => React.ReactNode = (value) => {
        const index = priorityOptions.findIndex(p => p.id === value.id);
        if (index === -1 || value.id === -1)
            return <em>None</em>;
        return priorityOptions[index].name;
    };

    const onAddProblemLogClick = () => {
        setAddProblemModal(!addProblemModal);
    };

    const hadleProblemTextChange = (event: any) => {
        setProblemTextField(event.target.value);
    };

    const handleProblemTextChange = (event: any) => {
        const { value } = event.target;
        if (value.length > 300) {
            setProblemError('Allows maximum 300 characters.');
        } else {
            setProblemError('');
        }
        setProblemTextField(value);
    };

    const onSaveNewProblem = () => {
        setProblemToUpdate({ description: problemTextField, resolution: false });
        setAddProblemModal(!addProblemModal);
        setProblemTextField(undefined);
        if (task) updateTaskOverview({ ...task, isProblem: true })
    };

    const onUpdateProblem = (problemLogId: number, resolution: boolean, problemDescription?: string) => {
        if (resolution === undefined) {
            if (problemLog !== null && problemLog !== undefined) {
                resolution = problemLog.find(p => p.id === problemLogId)?.isProblemResolved || false;
            }
            else {
                resolution = false;
            }
        }

        setProblemToUpdate({ problemLogId: problemLogId, description: problemDescription, resolution: resolution });
        if (task) updateTaskOverview({ ...task, isProblem: !resolution })
        setProblemTextField(undefined);
    };

    const onCancelSaveProblem = () => {
        setProblemTextField(undefined);
        setAddProblemModal(false);
    };

    const canChangeStatus = task && permissions && permissions.canChangeStatus
        && (task.statusId < 6 || permissions.canReopenCompleted);

    const taskIsCompleted = task?.statusId === 6;
    const DrawerTitle = styled('div')(({ theme }) => ({
        fontFamily: 'Open Sans',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '18px',
        lineHeight: '30px',
        color: '#333333',
        marginBottom: '40px'
    }));

    return (
        <>
            {/* Main wrapper for task overview with explicit role and tabIndex */}
            <Grid container spacing={2} role="main" tabIndex={0} aria-label="Task overview form">
                <Grid item xs={12}>
                    <Box>
                        <Grid container spacing={1} >
                            <Grid item sx={{ width: 150, minWidth: 100, pr: 2, textAlign: "right" }}>
                                <KeyGrid>Status:</KeyGrid>
                            </Grid>
                            <Grid item sx={{ flex: 1 }}>
                                <CentralDropdown
                                    readonly={!canChangeStatus}
                                    tabIndex={0}
                                    value={task?.statusId}
                                    applyValue={saveStatus}
                                    options={statusOptions}
                                    working={statusWorking}
                                    aria-label="Task Status"
                                />
                            </Grid>
                        </Grid>

                        {task?.statusId === 2 && (
                            <Grid container spacing={1} >
                                <Grid item sx={{ width: 150, minWidth: 100, pr: 2, textAlign: "right" }}>
                                    <KeyGrid>Progress:</KeyGrid>
                                </Grid>
                                <Grid item sx={{ flex: 1 }}>
                                    <TaskProgressPill progress={task?.progress} />
                                </Grid>
                            </Grid>
                        )}

                        <Grid container spacing={1} >
                            <Grid item sx={{ width: 150, minWidth: 100, pr: 2, textAlign: "right" }}>
                                <KeyGrid>Flag a Problem?</KeyGrid>
                            </Grid>
                            <Grid item sx={{ flex: 1 }}>
                                <Button
                                    onClick={onAddProblemLogClick}
                                    disabled={(task?.statusId === 6 || task?.statusId === 7) || !hasAddProblemFlagPermission || loading || saveProblemLoading || (problemLog && problemLog.length > 0)}
                                    variant="contained"
                                    size="small"
                                    color="primary"
                                    sx={{ height: '24px', ml: '-2px' }}
                                    title="Add Problem"
                                    aria-label="Add Problem Flag"
                                    tabIndex={2}
                                >
                                    Add
                                </Button>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            {!loading ? (
                                hasViewProblemLogPermission && (
                                    <TaskProblemGrid
                                        problems={problemLog}
                                        onUpdateProblem={onUpdateProblem}
                                        hasAddProblemFlagPermission={hasAddProblemFlagPermission}
                                        hasRemoveProblemFlagPermission={hasRemoveProblemFlagPermission}
                                        isTaskCompleted={task?.statusId === 6 || task?.statusId === 7}
                                    />
                                )
                            ) : (
                                <CircularProgress sx={{ position: 'relative', my: '10px' }} color="primary" />
                            )}
                        </Grid>

                        <Grid container spacing={1} >
                            <Grid item sx={{ width: 150, minWidth: 100, pr: 2, textAlign: "right" }}>
                                <KeyGrid>Duration:</KeyGrid>
                            </Grid>
                            <Grid item sx={{ flex: 1 }}>
                                <CentralTextField
                                    tabIndex={3}
                                    value={`${task?.duration.toString()} day(s)`}
                                    type="number"
                                    applyValue={saveDuration}
                                    working={durationWorking}
                                    readonly={task?.endDateLocked || taskIsCompleted || (!permissions?.canIncreaseDuration && !permissions?.canDecreaseDuration)}
                                    endAdornment={<InputAdornment position="end">day(s)</InputAdornment>}
                                    aria-label="Task Duration in Days"
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={1} >
                            <Grid item sx={{ width: 150, minWidth: 100, pr: 2, textAlign: "right" }}>
                                <KeyGrid>Start Date:</KeyGrid>
                            </Grid>
                            <Grid item sx={{ flex: 1 }}>
                                <CentralDatePicker
                                    tabIndex={4}
                                    value={moment.utc(task?.startDate).toString()}
                                    working={startDateWorking}
                                    applyValue={saveStartDate}
                                    readonly={task?.startDateLocked || taskIsCompleted || !task?.canEditStartDate || (!permissions?.canIncreaseDuration && !permissions?.canDecreaseDuration)}
                                    locked={task?.startDateLocked}
                                    aria-label="Task Start Date"
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={1} >
                            <Grid item sx={{ width: 150, minWidth: 100, pr: 2, textAlign: "right" }}>
                                <KeyGrid>End Date:</KeyGrid>
                            </Grid>
                            <Grid item sx={{ flex: 1 }}>
                                <CentralDatePicker
                                    tabIndex={5}
                                    value={moment.utc(task?.endDate).toString()}
                                    working={endDateWorking}
                                    applyValue={saveEndDate}
                                    readonly={task?.endDateLocked || taskIsCompleted || (!permissions?.canIncreaseDuration && !permissions?.canDecreaseDuration)}
                                    locked={task?.endDateLocked}
                                    aria-label="Task End Date"
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={1} >
                            <Grid item sx={{ width: 150, minWidth: 100, pr: 2, textAlign: "right" }}>
                                <KeyGrid>Responsibility:</KeyGrid>
                            </Grid>
                            <Grid item sx={{ flex: 1 }}>
                                <CentralDropdown
                                    tabIndex={6}
                                    value={task?.responsibilityId}
                                    applyValue={saveResponsibility}
                                    options={template?.responsibilities || []}
                                    working={responsibilityWorking}
                                    readonly={taskIsCompleted || !permissions?.canChangeResources}
                                    aria-label="Task Responsibility"
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={1} >
                            <Grid item sx={{ width: 150, minWidth: 100, pr: 2, textAlign: "right" }}>
                                <KeyGrid>Resource:</KeyGrid>
                            </Grid>
                            <Grid item sx={{ flex: 1 }}>
                                <CentralDropdown
                                    tabIndex={7}
                                    value={task?.resourceId}
                                    applyValue={saveResource}
                                    options={resourceOptions}
                                    working={resourceWorking}
                                    readonly={taskIsCompleted || !permissions?.canChangeResources}
                                    aria-label="Task Resource"
                                />
                            </Grid>
                        </Grid>

                        {task?.needHelpName &&
                            <Grid container spacing={1} >
                                <Grid item sx={{ width: 150, minWidth: 100, pr: 2, textAlign: "right" }}>
                                    <KeyGrid>Need Help?</KeyGrid>
                                </Grid>
                                <Grid item display='flex'>
                                    <Link target='_blank' sx={{ display: 'flex', fontSize: '16px' }} href={task?.needHelpLink}>
                                        {task?.needHelpName} 
                                    </Link>
                                    &nbsp;
                                    <Ico_NewWindow sx={{height: '20px', width: '20px'}} />
                                </Grid>
                            </Grid>
                        }
                        
                    </Box>
                </Grid>
            </Grid>

            <Divider />

            <Drawer
                anchor={'right'}
                open={addProblemModal}
                onClose={onCancelSaveProblem}
                PaperProps={{
                    sx: {
                        width: '30%',
                        backgroundColor: "rgba(255, 255, 255, 1)"
                    }
                }}
                sx={{ zIndex: 1300 }}>
                <Box>
                    <Grid container  >
                        <Grid xs={10}>
                            <DrawerTitle>Add Problem</DrawerTitle>
                        </Grid>
                        <Grid xs={2}>
                            <Link sx={{ float: 'right' }} onClick={onCancelSaveProblem}>
                                <Ico_Close />
                            </Link>
                        </Grid>
                        <Grid xs={12}>
                            <TextField
                                onChange={handleProblemTextChange}
                                multiline
                                maxRows={4}
                                variant="outlined"
                                label='Description'
                                sx={{ width: '100%', maxHeight: '15rem', '& .MuiInputBase-root': { height: '185px' }, '& .MuiInputBase-input': { fontSize: '14px' } }}
                                error={problemError !== ''}
                                helperText={problemError} />
                            {saveProblemError && <Alert severity="error">Something went wrong trying to save, please try again later</Alert>}

                            <DialogActions sx={{ paddingTop: '20px' }}>
                                <Button variant="contained" disabled={(problemTextField === undefined || problemTextField.length < 1 || problemError !== '')} onClick={onSaveNewProblem}>Save</Button>
                                <Button variant="outlined" onClick={onCancelSaveProblem}>Cancel</Button>
                            </DialogActions>
                        </Grid>
                    </Grid>
                </Box>
            </Drawer>
        </>
    );
};

export default TaskOverview;